import { BillingDetails } from '@aaa-ncnu-ie/mypolicy-api-customers';
import { Stack, Typography, useMediaQuery } from '@mui/material';
import { useMemo, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useBillsQuery } from '../../api/getBills';
import { useWelcomeQuery } from '../../api/getWelcomeDetails';
import { useOTSPQuery } from '../../api/oneTimeScheduledPaymentInfo';
import StyledButton from '../../components/styledComponents/Button';
import colors from '../../constants/colors';
import { PaymentAmountType } from '../../constants/enums';
import { usePaymentsContext } from '../../store/payments';
import {
  SelectedPayment,
  setInitialMultiPayments,
} from '../../store/paymentsReducer';
import theme from '../../styles/theme';
import PaymentCard from './components/PaymentCard';
import PaymentShoppingCart from './components/PaymentShoppingCart';

const MultiplePolicyPayments = () => {
  const { data } = useWelcomeQuery();
  const { data: billsData } = useBillsQuery();
  const { data: otspData } = useOTSPQuery(data?.policies);
  const { state: paymentState, dispatch } = usePaymentsContext();
  const paymentStateList = Object.keys(paymentState.selectedPayments);
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('desktop'));
  const navigate = useNavigate();

  const bills = billsData?.payments;
  const billsWithPayment = useMemo(() => {
    const sortBillsByMinimumDue = (a: BillingDetails, b: BillingDetails) => {
      const aMinDue = parseFloat(a.minimumDueAmount) > 0;
      const bMinDue = parseFloat(b.minimumDueAmount) > 0;
      if (aMinDue && !bMinDue) {
        return -1;
      } else if (bMinDue && !aMinDue) {
        return 1;
      } else {
        return 0;
      }
    };

    return (
      bills
        ?.filter(
          (bill) =>
            bill &&
            (parseFloat(bill.minimumDueAmount) > 0 ||
              parseFloat(bill.totalDueAmount) > 0)
        )
        .sort(sortBillsByMinimumDue) ?? []
    );
  }, [bills]);

  useEffect(() => {
    if (
      billsWithPayment?.length > 0 &&
      Object.keys(paymentState.selectedPayments).length < 1
    ) {
      const initialMultiPaymentState: {
        [policyNumber: string]: SelectedPayment;
      } = billsWithPayment?.reduce((initialPaymentState, bill) => {
        const paymentType =
          parseFloat(bill?.minimumDueAmount) > 0
            ? PaymentAmountType.TOTAL_DUE
            : PaymentAmountType.NO_PAYMENT;

        return {
          ...initialPaymentState,
          [bill.policyNumber]: {
            ...bill,
            policyType: bill.policyType,
            paymentType,
            amount:
              parseFloat(bill.minimumDueAmount) > 0
                ? bill.minimumDueAmount
                : '0',
          },
        };
      }, {});
      dispatch(setInitialMultiPayments(initialMultiPaymentState));
    }
  }, [paymentStateList]);

  return (
    <Stack
      data-testid="multiplePaymentPage"
      sx={{
        width: 1,
        minHeight: '100%',
        px: { mobile: 2, desktop: 7 },
        pt: { mobile: 3, desktop: 9 },
        pb: 3,
      }}
    >
      <Typography
        variant="h1"
        sx={{
          fontSize: { mobile: 33, tablet: 44 },
          lineHeight: { mobile: '48px', desktop: '56px' },
          letterSpacing: 0.6,
          fontWeight: 500,
          color: colors.csaaBlack,
          mb: { mobile: 2, tablet: 3 },
        }}
      >
        How much would you like to pay?
      </Typography>
      <Stack direction="row" spacing={3} sx={{
        width: 1
      }}>
        <Stack spacing={3} sx={{
          width: 1
        }}>
          {billsWithPayment.length > 0 &&
            paymentStateList.length > 0 &&
            billsWithPayment.map((billItem, index) => (
              <PaymentCard
                key={`payment-card-${index}`}
                bill={billItem}
                cardIndex={index}
                paymentAmount={
                  paymentState.selectedPayments?.[billItem.policyNumber]
                }
                scheduledPayment={otspData?.[billItem.policyNumber]}
              />
            ))}
          {isMobileOrTablet && (
            <Stack
              direction={{ mobile: 'column', tablet: 'row' }}
              spacing={2}
              sx={{
                alignSelf: "flex-end",
                width: { mobile: 1, tablet: 'auto' }
              }}>
              <StyledButton
                component={Link}
                relative="path"
                to=".."
                data-testid="payment-continue-button"
                variant="outlined"
                sx={{
                  borderWidth: 2,
                  fontWeight: 700,
                  textTransform: 'none',
                  fontSize: 18,
                  px: { mobile: 4, desktop: 3 },
                  mt: { mobile: 5 },
                  wordWrap: 'nowrap',
                  color: colors.csaaBlack,
                  borderColor: colors.csaaBlack,
                  whiteSpace: 'nowrap',
                }}
                onClick={() => {
                  navigate(-1);
                }}
              >
                Cancel
              </StyledButton>
              <StyledButton
                component={Link}
                to="selected-payments"
                disabled={
                  !Object.values(paymentState.selectedPayments).find(
                    (payment) => parseFloat(payment.amount ?? '0') > 0
                  )
                }
                sx={{ mt: 3 }}
              >
                Continue
              </StyledButton>
            </Stack>
          )}
        </Stack>
        {billsWithPayment?.length > 0 && !isMobileOrTablet && (
          <PaymentShoppingCart />
        )}
      </Stack>
    </Stack>
  );
};

export default MultiplePolicyPayments;
